// Thin API client. Real feature PRs should replace the ad-hoc `fetch`
// here with a typed client (openapi-fetch, ts-rest, or hand-rolled from
// backbone-bucket/sapiens OpenAPI specs).

const baseUrl: string =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000";

export interface HealthResponse {
  status: string;
  service: string;
  timestamp: string;
}

// --------------------------------------------------------------------
// Maintenance-mode contract (mirrors apps/bersihir-service)
//
// When the service is under maintenance, gated requests return:
//   - HTTP 503
//   - Header `X-Service-Status: maintenance`
//   - Header `Retry-After: <seconds>`
//   - JSON body matching `MaintenanceInfo` below.
//
// Use `apiFetch` instead of bare `fetch` so a uniform `MaintenanceError`
// is thrown across the webapp — the root error boundary should catch
// this and render a maintenance banner with severity-driven styling.
//
// For proactive polling (e.g. on app boot, before showing the landing
// page), call `getMaintenanceStatus()` — that endpoint is always 200
// and never gated, even while maintenance is on.
// --------------------------------------------------------------------

export interface MaintenanceInfo {
  status: "maintenance";
  message: string;
  retry_after: number;
  severity: string;
  started_at: string | null;
  estimated_end_at: string | null;
}

export interface MaintenanceStatus {
  enabled: boolean;
  message: string;
  retry_after_seconds: number;
  severity: string;
  started_at: string | null;
  estimated_end_at: string | null;
}

export class MaintenanceError extends Error {
  readonly info: MaintenanceInfo;
  constructor(info: MaintenanceInfo) {
    super(info.message);
    this.name = "MaintenanceError";
    this.info = info;
  }
}

/**
 * Detect the maintenance signal on a Response. Returns the parsed body when
 * the response carries the maintenance contract, otherwise `null`.
 *
 * Cloning the response here keeps the body readable by the caller, so this
 * can be used to *inspect without consuming*.
 */
export async function detectMaintenance(
  resp: Response,
): Promise<MaintenanceInfo | null> {
  if (resp.status !== 503) return null;
  if (resp.headers.get("X-Service-Status")?.toLowerCase() !== "maintenance") {
    return null;
  }
  try {
    return (await resp.clone().json()) as MaintenanceInfo;
  } catch {
    return null;
  }
}

/**
 * Drop-in replacement for `fetch` that throws `MaintenanceError` when the
 * service is in maintenance mode. Other 4xx/5xx responses pass through
 * unchanged for the caller to handle.
 */
export async function apiFetch(
  input: string,
  init?: RequestInit,
): Promise<Response> {
  const url = input.startsWith("http") ? input : `${baseUrl}${input}`;
  const resp = await fetch(url, init);
  const info = await detectMaintenance(resp);
  if (info) throw new MaintenanceError(info);
  return resp;
}

/**
 * Poll the always-200 `/maintenance/status` endpoint. Use this on app boot
 * (and optionally on a slow interval) to surface a banner *before* the user
 * triggers a gated request.
 */
export async function getMaintenanceStatus(): Promise<MaintenanceStatus> {
  const resp = await fetch(`${baseUrl}/maintenance/status`, {
    method: "GET",
    credentials: "omit",
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as MaintenanceStatus;
}

export async function pingHealth(): Promise<HealthResponse> {
  const resp = await apiFetch("/health", {
    method: "GET",
    credentials: "omit",
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as HealthResponse;
}

/** Attach an `Authorization: Bearer <token>` header to a fetch init. */
export function withBearer(init: RequestInit, token: string): RequestInit {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}
